import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    MdArrowBack,
    MdLocalShipping,
    MdCheckCircle,
    MdAssignment,
    MdInfo,
} from 'react-icons/md';
import { authenticatedFetch } from '../../lib/api';
import { cn } from '../../lib/utils';
import { useToast } from '../../components/shared/Toast';
import { AdminInvoicesSection } from '../../components/orders/AdminInvoicesSection';
import { AdminShippingChargesSection } from '../../components/orders/AdminShippingChargesSection';
import { AdminBuyerInvoicesSection } from '../../components/orders/AdminBuyerInvoicesSection';
import { AdminBuyerShippingSection } from '../../components/orders/AdminBuyerShippingSection';
import { AdminBuyerPaymentSection } from '../../components/orders/AdminBuyerPaymentSection';
import { PageSkeleton } from '@euroasiann/shared/ui';
import { useAuth } from '../../context/AuthContext';

interface POItem {
    itemDescription: string;
    quantity: number;
    unitPrice: number;
    total: number;
    uom: string;
}

interface PurchaseOrder {
    _id: string;
    poNumber: string;
    title: string;
    status: string;
    totalAmount: number;
    currency: string;
    createdAt: string;
    organizationId: { name: string; email?: string; phone?: string };
    vendorOrganizationId: { name: string; email?: string; phone?: string };
    items: POItem[];
    metadata?: any;
    shippingMode?: 'vendor' | 'self' | null;
    shippingDetails?: any;
    quotationId?: string | {
        _id: string;
        metadata?: any;
        items?: any[];
        totalAmount?: number;
        currency?: string;
        advancePercentage?: number;
    };
    advanceAmount?: number;
    advancePercentage?: number;
}

function orderWorkflowLabel(po: PurchaseOrder): string | null {
    if (!po.metadata?.isAdminOffer) return null;
    const meta = po.metadata || {};
    if (meta.paymentUnlocked || meta.finalInvoiceIssued) {
        return 'Final invoice issued — payment unlocked';
    }
    if (meta.proformaInvoiceApproved) {
        if (po.shippingMode === 'self') return 'Proforma approved — self shipment confirmed';
        if (meta.adminShippingRequested) {
            return 'Customer asked for shipping charges — configure and issue final invoice';
        }
        if (!po.shippingMode) return 'Proforma approved — await customer shipping choice';
        if (po.shippingMode === 'vendor') {
            const hasCharges =
                Array.isArray(po.shippingDetails?.vendorCharges) &&
                po.shippingDetails.vendorCharges.some((c: any) => String(c?.label || '').trim());
            if (!hasCharges) return 'Configure shipping charges, then issue final invoice';
        }
        return 'Issue final invoice (includes shipping if any)';
    }
    if (meta.proformaInvoiceSent) {
        return 'Proforma sent — awaiting customer approval';
    }
    return 'Send proforma invoice (item prices only)';
}

export function PODetailsPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    const { user } = useAuth();
    const [shippingPrefill, setShippingPrefill] = useState<{
        charges: Array<{ label: string; amount: string; chargeType: 'shipping' | 'packing' | 'custom' | 'other' }>;
        taxes: Array<{ label: string; rate: string; amount: string }>;
        notes?: string;
    } | null>(null);

    const { data: po, isLoading, error } = useQuery<PurchaseOrder>({
        queryKey: ['admin-po-details', id],
        queryFn: async () => {
            if (!id) throw new Error('PO ID is required');
            const res = await authenticatedFetch(`/api/v1/admin/pos/${id}`);
            if (!res.ok) throw new Error('Failed to fetch PO details');
            const data = await res.json();
            return data.data;
        },
        enabled: !!id,
    });

    const { data: linkedShipment } = useQuery({
        queryKey: ['admin-po-shipment', po?.poNumber],
        queryFn: async () => {
            if (!po?.poNumber) return null;
            const res = await authenticatedFetch(
                `/api/v1/admin/shipments?search=${encodeURIComponent(po.poNumber)}`
            );
            if (!res.ok) return null;
            const json = await res.json();
            const list = json.data?.shipments || [];
            return (
                list.find((s: any) => s.orderNumber === po.poNumber) ||
                list[0] ||
                null
            );
        },
        enabled: !!po?.poNumber,
    });

    const acknowledgeMutation = useMutation({
        mutationFn: async () => {
            const res = await authenticatedFetch(`/api/v1/admin/pos/${id}/acknowledge`, {
                method: 'POST',
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || 'Failed to acknowledge PO');
            }
            return res.json();
        },
            onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-po-details', id] });
            queryClient.invalidateQueries({ queryKey: ['admin-pos'] });
            showToast('Purchase order acknowledged', 'success');
        },
        onError: (e: Error) => showToast(e.message, 'error'),
    });

    if (isLoading) {
        return <PageSkeleton variant="cards" />;
    }

    if (error || !po) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
                <div className="p-4 bg-destructive/10 text-destructive rounded-full">
                    <MdAssignment className="w-12 h-12" />
                </div>
                <h2 className="text-2xl font-bold italic text-destructive">Order Not Found</h2>
                <p className="text-muted-foreground max-w-md text-center">
                    We couldn't find the purchase order you're looking for.
                </p>
                <button
                    onClick={() => navigate('/dashboard/admin/pos/received')}
                    className="mt-4 px-6 py-2 bg-primary text-primary-foreground rounded-xl font-bold shadow-lg hover:scale-105 transition-transform"
                >
                    Return to Purchase Orders
                </button>
            </div>
        );
    }

    const statusVariants: Record<string, { bg: string; text: string; icon: any }> = {
        draft: { bg: 'bg-[#0088cc]/10', text: 'text-[hsl(var(--foreground))]', icon: MdAssignment },
        issued: { bg: 'bg-blue-100', text: 'text-[#0077b3]', icon: MdAssignment },
        acknowledged: { bg: 'bg-amber-100', text: 'text-amber-700', icon: MdCheckCircle },
        shipped: { bg: 'bg-purple-100', text: 'text-purple-700', icon: MdLocalShipping },
        delivered: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: MdCheckCircle },
    };

    const currentStatus = statusVariants[po.status.toLowerCase()] || statusVariants.draft;
    const StatusIcon = currentStatus.icon;
    const isAdminOfferPo = po.metadata?.isAdminOffer === true;
    const myOrgId = String(
        (user?.organizationId as any)?._id || user?.organizationId || ''
    );
    const buyerOrgId = String(
        (po.organizationId as any)?._id || po.organizationId || ''
    );
    const vendorOrgId = String(
        (po.vendorOrganizationId as any)?._id || po.vendorOrganizationId || ''
    );
    const isBuyerPo = !isAdminOfferPo && !!myOrgId && buyerOrgId === myOrgId;
    const isReceivedVendorPo =
        !isBuyerPo && ((!!myOrgId && vendorOrgId === myOrgId) || isAdminOfferPo);
    const canAcknowledge =
        isReceivedVendorPo && ['issued', 'draft'].includes(po.status.toLowerCase());
    const showFulfillmentSections = !['draft', 'issued', 'cancelled'].includes(po.status.toLowerCase());
    const workflowLabel = orderWorkflowLabel(po);

    return (
        <div className="w-full p-8 space-y-8 animate-in slide-in-from-bottom duration-500">
            {isAdminOfferPo && workflowLabel && (
                <div className="flex items-start gap-3 p-4 rounded-2xl border border-[#0088cc]/25 bg-[#0088cc]/5 text-sm">
                    <MdInfo className="w-5 h-5 text-[#0088cc] shrink-0 mt-0.5" />
                    <div>
                        <p className="font-bold text-[hsl(var(--foreground))]">Offer order workflow</p>
                        <p className="text-[hsl(var(--muted-foreground))] mt-1">{workflowLabel}</p>
                    </div>
                </div>
            )}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-8">
                <div className="space-y-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-2 group"
                    >
                        <MdArrowBack className="group-hover:-translate-x-1 transition-transform" />
                        <span className="text-sm font-semibold uppercase tracking-wider">Back to List</span>
                    </button>
                    <div className="flex items-center gap-4">
                        <h1 className="text-4xl font-black tracking-tight">{po.poNumber}</h1>
                        <span
                            className={cn(
                                'flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest ring-1 ring-inset',
                                currentStatus.bg,
                                currentStatus.text
                            )}
                        >
                            <StatusIcon className="w-4 h-4" />
                            {po.status}
                        </span>
                    </div>
                    <p className="text-lg text-muted-foreground font-medium">{po.title}</p>
                </div>
                <div className="flex flex-wrap gap-3">
                    {canAcknowledge && (
                        <button
                            type="button"
                            onClick={() => acknowledgeMutation.mutate()}
                            disabled={acknowledgeMutation.isPending}
                            className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 text-white rounded-xl font-bold hover:bg-amber-700 disabled:opacity-50"
                        >
                            <MdCheckCircle className="w-5 h-5" />
                            {acknowledgeMutation.isPending ? 'Acknowledging…' : 'Acknowledge PO'}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() =>
                            navigate(`/dashboard/admin/shipments?search=${encodeURIComponent(po.poNumber)}`)
                        }
                        className="flex items-center gap-2 px-5 py-2.5 bg-card border rounded-xl font-bold hover:bg-muted transition-colors shadow-sm"
                    >
                        <MdLocalShipping className="w-5 h-5" />
                        Open Shipments
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-card rounded-2xl border p-6 shadow-sm space-y-6">
                        <div className="space-y-4">
                            <h3 className="text-xs font-black text-muted-foreground uppercase tracking-[0.2em]">
                                Organization
                            </h3>
                            <div className="p-4 bg-muted/30 rounded-xl border border-dashed border-border">
                                <p className="font-black text-lg">{po.organizationId?.name}</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    {po.organizationId?.email || 'No email provided'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {(() => {
                        const itemsSubtotal = (po.items || []).reduce(
                            (sum, item) => sum + Number(item.total || (item.quantity || 0) * (item.unitPrice || 0)),
                            0
                        );
                        const charges = Array.isArray(po.shippingDetails?.vendorCharges)
                            ? po.shippingDetails.vendorCharges.filter((c: any) => String(c?.label || '').trim())
                            : [];
                        const taxes = Array.isArray(po.shippingDetails?.taxes)
                            ? po.shippingDetails.taxes.filter((t: any) => String(t?.label || '').trim())
                            : [];
                        const chargesSubtotal =
                            Number(po.shippingDetails?.chargesSubtotal) ||
                            charges.reduce((sum: number, c: any) => sum + Number(c.amount || 0), 0);
                        const taxTotal =
                            Number(po.shippingDetails?.taxTotal) ||
                            taxes.reduce((sum: number, t: any) => sum + Number(t.amount || 0), 0);
                        const quotation =
                            po.quotationId && typeof po.quotationId === 'object' ? po.quotationId : null;
                        const quoteItems = Array.isArray(quotation?.metadata?.items)
                            ? quotation.metadata.items
                            : Array.isArray(quotation?.items)
                              ? quotation.items
                              : [];
                        const quoteDiscount = quoteItems.reduce(
                            (sum: number, item: any) => sum + Number(item.discountAmount || 0),
                            0
                        );
                        const itemTaxByLabel = quoteItems.reduce((acc: Record<string, number>, item: any) => {
                            const qty = Number(item.offeredQty || item.quantity || 0);
                            const price = Number(item.quotedPrice || item.unitPrice || 0);
                            const discPercent = Number(item.discountPercent || 0);
                            const taxPercent = Number(item.taxPercent || 0);
                            const base = qty * price;
                            const disc = Number(item.discountAmount || 0) || base * (discPercent / 100);
                            const taxAmt = (base - disc) * (taxPercent / 100);
                            if (taxAmt <= 0) return acc;
                            const label = taxPercent
                                ? `GST / VAT (${taxPercent}%)`
                                : 'GST / VAT';
                            acc[label] = (acc[label] || 0) + taxAmt;
                            return acc;
                        }, {} as Record<string, number>);
                        const taxTerm = quotation?.metadata?.terms?.taxTerm;
                        const fmt = (n: number) =>
                            n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        const grand = Number(po.totalAmount || 0);
                        const advance = Number(po.advanceAmount || 0);
                        const balance = Math.max(0, grand - advance);

                        return (
                    <div className="bg-primary/5 rounded-2xl border border-primary/20 p-6 shadow-sm space-y-4">
                        <h3 className="text-xs font-black text-primary uppercase tracking-[0.2em]">
                            Financial Breakdown
                        </h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between gap-4">
                                <span className="text-muted-foreground">Items subtotal</span>
                                <span className="font-semibold">{po.currency} {fmt(itemsSubtotal)}</span>
                            </div>
                            {quoteDiscount > 0 && (
                                <div className="flex justify-between gap-4">
                                    <span className="text-muted-foreground">Discount</span>
                                    <span className="font-semibold">-{po.currency} {fmt(quoteDiscount)}</span>
                                </div>
                            )}
                            {Object.entries(itemTaxByLabel).map(([label, amount]) => (
                                <div key={label} className="flex justify-between gap-4">
                                    <span className="text-muted-foreground">{label}</span>
                                    <span className="font-semibold">{po.currency} {fmt(amount)}</span>
                                </div>
                            ))}
                            {charges.map((c: any, idx: number) => (
                                <div key={`chg-${idx}`} className="flex justify-between gap-4">
                                    <span className="text-muted-foreground">{c.label}</span>
                                    <span className="font-semibold">{po.currency} {fmt(Number(c.amount || 0))}</span>
                                </div>
                            ))}
                            {charges.length === 0 && chargesSubtotal > 0 && (
                                <div className="flex justify-between gap-4">
                                    <span className="text-muted-foreground">Shipping & packing</span>
                                    <span className="font-semibold">{po.currency} {fmt(chargesSubtotal)}</span>
                                </div>
                            )}
                            {taxes.map((t: any, idx: number) => (
                                <div key={`tax-${idx}`} className="flex justify-between gap-4">
                                    <span className="text-muted-foreground">
                                        {t.label}{t.rate ? ` (${t.rate}%)` : ''}
                                    </span>
                                    <span className="font-semibold">{po.currency} {fmt(Number(t.amount || 0))}</span>
                                </div>
                            ))}
                            {taxes.length === 0 && taxTotal > 0 && (
                                <div className="flex justify-between gap-4">
                                    <span className="text-muted-foreground">Tax</span>
                                    <span className="font-semibold">{po.currency} {fmt(taxTotal)}</span>
                                </div>
                            )}
                            {taxTerm && (
                                <div className="flex justify-between gap-4">
                                    <span className="text-muted-foreground">Tax terms</span>
                                    <span className="font-semibold text-right">{String(taxTerm)}</span>
                                </div>
                            )}
                            {advance > 0 && (
                                <>
                                    <div className="flex justify-between gap-4">
                                        <span className="text-muted-foreground">
                                            Advance{po.advancePercentage ? ` (${po.advancePercentage}%)` : ''}
                                        </span>
                                        <span className="font-semibold">{po.currency} {fmt(advance)}</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                        <span className="text-muted-foreground">Balance</span>
                                        <span className="font-semibold">{po.currency} {fmt(balance)}</span>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="pt-2 border-t border-primary/20 flex justify-between items-end">
                            <span className="text-lg font-black tracking-tight uppercase">Total</span>
                            <div className="text-right">
                                <span className="text-sm font-bold text-primary block">{po.currency}</span>
                                <p className="text-4xl font-black text-primary tracking-tighter">
                                    {fmt(grand)}
                                </p>
                            </div>
                        </div>
                    </div>
                        );
                    })()}

                    <div className="bg-card rounded-2xl border p-6 shadow-sm space-y-4">
                        <h3 className="text-xs font-black text-muted-foreground uppercase tracking-[0.2em] flex items-center gap-2">
                            <MdLocalShipping className="w-4 h-4" />
                            Logistics
                        </h3>
                        {linkedShipment ? (
                            <div className="space-y-3">
                                <p className="text-sm">
                                    Status:{' '}
                                    <span className="font-bold uppercase">{linkedShipment.status}</span>
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    Tracking: {linkedShipment.trackingNumber || 'Not set'}
                                </p>
                                <button
                                    type="button"
                                    onClick={() =>
                                        navigate(
                                            `/dashboard/admin/shipments?id=${linkedShipment._id}&search=${encodeURIComponent(po.poNumber)}`
                                        )
                                    }
                                    className="w-full py-2.5 bg-primary text-primary-foreground rounded-xl text-xs font-bold"
                                >
                                    View / Update Shipment
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <p className="text-sm text-muted-foreground">
                                    No shipment yet. The vendor initializes tracking from the vendor
                                    portal after payment is approved.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-card rounded-2xl border overflow-hidden shadow-sm">
                        <div className="p-6 border-b bg-muted/30">
                            <h3 className="text-lg font-black flex items-center gap-2">
                                <MdAssignment className="text-primary w-6 h-6" />
                                Order Items
                            </h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-muted/50 text-[10px] font-black uppercase tracking-widest border-b">
                                    <tr>
                                        <th className="px-6 py-4">Item Description</th>
                                        <th className="px-6 py-4 text-center">Qty / UOM</th>
                                        <th className="px-6 py-4 text-right">Unit Price</th>
                                        <th className="px-6 py-4 text-right">Total Price</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y text-sm">
                                    {po.items?.length > 0 ? (
                                        po.items.map((item, index) => (
                                            <tr key={index} className="hover:bg-muted/30 transition-colors">
                                                <td className="px-6 py-5 font-bold">{item.itemDescription}</td>
                                                <td className="px-6 py-5 text-center">
                                                    <span className="bg-muted px-3 py-1 rounded-lg font-black text-xs">
                                                        {item.quantity} {item.uom || 'PCS'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-5 text-right font-medium">
                                                    {po.currency} {item.unitPrice.toLocaleString()}
                                                </td>
                                                <td className="px-6 py-5 text-right font-black text-primary">
                                                    {po.currency} {item.total.toLocaleString()}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan={4}
                                                className="px-6 py-10 text-center text-muted-foreground italic font-medium"
                                            >
                                                No specific items were found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {id && showFulfillmentSections && isAdminOfferPo && (
                        <AdminShippingChargesSection
                            po={po}
                            poId={id}
                            prefill={shippingPrefill}
                            onPrefillApplied={() => setShippingPrefill(null)}
                        />
                    )}

                    {id && showFulfillmentSections && isBuyerPo && (
                        <>
                            <AdminBuyerInvoicesSection poId={id} po={po} />
                            <AdminBuyerShippingSection
                                po={po}
                                poId={id}
                                canConfigure={!['cancelled', 'completed'].includes(po.status.toLowerCase())}
                            />
                            <AdminBuyerPaymentSection poId={id} po={po} />
                        </>
                    )}

                    {id && showFulfillmentSections && !isBuyerPo && (
                        <AdminInvoicesSection
                            po={po}
                            poId={id}
                            onExtractedShippingCharges={isAdminOfferPo ? setShippingPrefill : undefined}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
